import { Button, Flex, Modal, Table, TableProps, Form } from 'antd';
import { useState, FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';

// components
import ConfirmModalComponent from 'components/confirmModal/confirmModal.component';
import ComponentHeader from 'components/componentHeader/componentHeader';
import ListButton from 'components/listButton/listButton';
import NoData from 'components/noData/noData';
import GuestForm from './guestForm';

// services
import { isRTL } from 'core/services/language';

// models
import { CommitteeMeetingGuest } from '../models/meetings';

// hooks
import useCompanySuspended from 'core/hooks/useCompanySuspended';

// components
import SuspendedTooltip from 'components/suspendedTooltip/suspendedTooltip';

interface MeetingGuestsProps {
	guests: CommitteeMeetingGuest[];
	onChange(guests: CommitteeMeetingGuest[]): void;
}

const MeetingGuests: FunctionComponent<MeetingGuestsProps> = ({ guests, onChange }) => {
	const { t } = useTranslation();

	const { suspendedProps } = useCompanySuspended();

	const [viewForm] = Form.useForm();
	const [addForm] = Form.useForm();
	const [editForm] = Form.useForm();

	const [viewModalOpen, setViewModalOpen] = useState(false);
	const [addModalOpen, setAddModalOpen] = useState(false);
	const [editModalOpen, setEditModalOpen] = useState(false);
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);

	const [tempGuestIndex, setTempGuestIndex] = useState<number | null>(null);

	const columns: TableProps<CommitteeMeetingGuest>['columns'] = [
		{
			title: t('COMMON.NAME'),
			dataIndex: 'name',
			key: 'name',
			render: (_, row) => (isRTL() ? row.name : (row.name_en ?? row.name)),
		},
		{
			title: t('ACTIONS.POSITION'),
			dataIndex: 'position',
			key: 'position',
		},
		{
			title: t('ACTIONS.ORGANIZATION'),
			dataIndex: 'organization',
			key: 'organization',
		},
		{
			title: t('ACTIONS.POSITION'),
			dataIndex: 'position',
			key: 'position',
		},
		{
			title: t('COMMON.ACTIONS'),
			dataIndex: '_',
			key: '_',
			render: (_, row, index) => (
				<Flex justify="center" gap={10}>
					<ListButton
						type="view"
						onClick={() => {
							viewForm.setFieldsValue(row);
							setViewModalOpen(true);
						}}
					/>
					<>
						<ListButton
							type="edit"
							{...suspendedProps}
							onClick={() => {
								editForm.setFieldsValue(row);
								setTempGuestIndex(index);
								setEditModalOpen(true);
							}}
						/>
						<ListButton
							type="delete"
							{...suspendedProps}
							onClick={() => {
								setTempGuestIndex(index);
								setDeleteModalOpen(true);
							}}
						/>
					</>
				</Flex>
			),
		},
	];

	const addHandler = async (guest: CommitteeMeetingGuest) => {
		onChange([...guests, guest]);
		setAddModalOpen(false);
		addForm.resetFields();
	};

	const editHandler = async (guest: CommitteeMeetingGuest) => {
		if (tempGuestIndex !== null) {
			const updatedGuests = [...guests];
			updatedGuests[tempGuestIndex] = guest;
			onChange(updatedGuests);
			setEditModalOpen(false);
			editForm.resetFields();
		}
	};

	const deleteHandler = async () => {
		if (tempGuestIndex !== null) {
			const updatedGuests = [...guests];
			updatedGuests.splice(tempGuestIndex, 1);
			onChange(updatedGuests);
			setDeleteModalOpen(false);
		}
	};

	return (
		<>
			<Flex justify="space-between" align="center">
				<ComponentHeader title={t('ACTIONS.GUEST_INVITATION')} subtitle={t('ACTIONS.EXTERNAL_BODIES_NO_VOTING_RIGHTS')} />

				<Button type="primary" size="middle" className="px-4" onClick={() => setAddModalOpen(true)}>
					{t('ACTIONS.INVITE')}
				</Button>
			</Flex>

			<Table
				rowKey={(record) => `${record.name}-${record.email}`}
				columns={columns}
				dataSource={guests}
				className="bordered"
				scroll={{ x: 600 }}
				locale={{ emptyText: <NoData /> }}
				pagination={false}
			/>

			{/* VIEW MODAL */}
			<Modal
				open={viewModalOpen}
				width={900}
				title={t('ACTIONS.GUEST_DETAILS')}
				onCancel={() => setViewModalOpen(false)}
				footer={[
					<Button key="discard" type="link" danger size="middle" onClick={() => setViewModalOpen(false)}>
						{t('COMMON.CLOSE')}
					</Button>,
				]}>
				<GuestForm form={viewForm} disabled />
			</Modal>

			{/* ADD MODAL */}
			<Modal
				open={addModalOpen}
				width={900}
				title={t('ACTIONS.ADD_NEW_GUEST')}
				onCancel={() => {
					addForm.resetFields();
					setAddModalOpen(false);
				}}
				footer={[
					<Button key="discard" type="link" danger size="middle" onClick={() => setAddModalOpen(false)}>
						{t('COMMON.CANCEL')}
					</Button>,
					<Button key="confirm" type="primary" size="middle" onClick={addForm.submit}>
						{t('COMMON.ADD')}
					</Button>,
				]}>
				<GuestForm form={addForm} onSubmit={addHandler} />
			</Modal>

			{/* EDIT MODAL */}
			<Modal
				open={editModalOpen}
				width={900}
				title={t('ACTIONS.EDIT_GUEST')}
				onCancel={() => {
					editForm.resetFields();
					setEditModalOpen(false);
				}}
				footer={[
					<Button key="discard" type="link" danger size="middle" onClick={() => setEditModalOpen(false)}>
						{t('COMMON.CANCEL')}
					</Button>,
					<SuspendedTooltip key="confirm">
						<Button key="confirm" type="primary" size="middle" onClick={editForm.submit}>
							{t('COMMON.UPDATE')}
						</Button>
					</SuspendedTooltip>,
				]}>
				<GuestForm form={editForm} onSubmit={editHandler} />
			</Modal>

			{/* DELETE MODAL */}
			<ConfirmModalComponent
				open={deleteModalOpen}
				title={t('ACTIONS.DELETE_GUEST')}
				subTitle={t('ACTIONS.CONFIRM_DELETE_GUEST')}
				confirmText={t('COMMON.DELETE')}
				confirmColor="red"
				onDiscard={() => setDeleteModalOpen(false)}
				onConfirm={deleteHandler}
			/>
		</>
	);
};

export default MeetingGuests;
